// Resolve the full user-message for an FCM "wake" push (insertOnlyIfMissing).
//
// Data-minimization: the FCM push is operational-only — it carries a wake signal
// + opaque identifiers (messageId) and NO customer content. To process the
// pending send, the receiver resolves the full message AFTER waking:
//   1. from Redux (already synced), else
//   2. by fetching it from the backend by id.
//
// A legacy fallback keeps the flow working if an older backend still inlines the
// serialized message (`messageData`) in the push — used only if present.

export interface FcmWakeData {
  messageId?: string;
  messageData?: string;
  [k: string]: unknown;
}

export interface FcmWakeResolverDeps<T = any> {
  /** Look the message up in local Redux state by _id (sync). */
  getFromRedux: (messageId: string) => T | undefined;
  /** Fetch the message from the backend by _id (network). */
  fetchById: (messageId: string) => Promise<T | undefined>;
}

/**
 * Returns the full message to process, or undefined if it cannot be resolved.
 * Prefers a locally-cached copy; only hits the network when necessary.
 */
export async function resolveFcmWakeMessage<T = any>(
  data: FcmWakeData | undefined,
  deps: FcmWakeResolverDeps<T>,
): Promise<T | undefined> {
  // Legacy back-compat: an older backend may still inline the message content.
  if (data?.messageData) {
    try {
      return JSON.parse(data.messageData) as T;
    } catch {
      // fall through to id-based resolution
    }
  }

  const messageId = data?.messageId;
  if (!messageId) return undefined;

  const cached = deps.getFromRedux(messageId);
  if (cached) return cached;

  try {
    return await deps.fetchById(messageId);
  } catch {
    return undefined;
  }
}

export type FcmWakeOutcome<R> =
  | { status: 'unresolved' }
  | { status: 'processed'; response: R };

/**
 * #547-adjacent: process an FCM wake by feeding the resolved message into the SAME wire handler the
 * MQTT path uses. The content script used to call `MQTTSubscriber.handleInsertOnlyIfMissingRequest`,
 * a method that was removed when the wire dispatch became the single handler table — so every FCM
 * wake threw "is not a function" and only MQTT carried the send. `run` is injected so the caller
 * owns the (heavy) handler import and tests stay free of the extension module graph.
 */
export async function processFcmWake<T extends { _id?: string }, R>(
  data: FcmWakeData | undefined,
  deps: FcmWakeResolverDeps<T>,
  run: (message: T) => Promise<R>,
): Promise<FcmWakeOutcome<R>> {
  const message = await resolveFcmWakeMessage<T>(data, deps);
  if (!message) return { status: 'unresolved' };
  return { status: 'processed', response: await run(message) };
}
